<template>
  <v-dialog v-model="isOpen" max-width="900" persistent>
    <v-card class="demo-scheduling-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="agent-info">
            <div class="agent-avatar">
              <i class="material-icons-round">event</i>
            </div>
            <div class="agent-details">
              <h3 class="agent-name">Demo Scheduler</h3>
              <p class="agent-title">AutoSyncX™ Demo Assistant</p>
            </div>
          </div>
          <div class="header-actions">
            <!-- Close Button -->
            <v-btn icon @click="closeModal" class="close-btn">
              <i class="material-icons-round">close</i>
            </v-btn>
          </div>
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="modal-content">
        <div class="chat-container">
          <div class="chat-messages" ref="chatMessages">
            <div v-for="message in messages" :key="message.id" class="message" :class="message.type">
              <div class="message-content">
                <div class="message-text" v-html="message.text"></div>
                <div class="message-time">{{ formatTime(message.timestamp) }}</div>
              </div>
            </div>
            <div v-if="isTyping" class="message agent typing">
              <div class="message-content">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Date Picker -->
          <div v-if="showDatePicker" class="date-picker-section">
            <h4>Select Your Preferred Date</h4>
            <v-date-picker
              v-model="selectedDate"
              :min="minDate"
              :max="maxDate"
              :allowed-dates="allowedDates"
              @input="onDateSelected"
              full-width
              color="primary"
              show-current
            ></v-date-picker>
          </div>

          <!-- Time Picker -->
          <div v-if="showTimePicker" class="time-picker-section">
            <h4>Available Times for {{ formatSelectedDate }}</h4>
            <div v-if="loadingTimes" class="text-center py-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">Loading available times...</p>
            </div>
            <div v-else-if="availableTimes.length === 0" class="text-center py-4">
              <p>No available times for this date. Please select another date.</p>
            </div>
            <div v-else class="time-slots-grid">
              <v-btn
                v-for="time in availableTimes"
                :key="time.value"
                :color="selectedTime === time.value ? 'primary' : 'default'"
                :outlined="selectedTime !== time.value"
                @click="selectTime(time.value)"
                class="time-slot-btn ma-2"
              >
                {{ time.label }}
              </v-btn>
            </div>
          </div>

          <div class="chat-input">
            <v-text-field
              v-model="newMessage"
              placeholder="Type your message..."
              outlined
              dense
              hide-details
              @keyup.enter="sendMessage"
              :disabled="isLoading"
            >
              <template v-slot:append>
                <v-btn 
                  icon 
                  @click="sendMessage" 
                  :disabled="!newMessage.trim() || isLoading"
                  class="send-btn"
                >
                  <i class="material-icons-round">send</i>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  name: 'DemoSchedulingModal',
  data() {
    return {
      isOpen: true,
      messages: [],
      newMessage: '',
      isLoading: false,
      isTyping: false,
      conversationId: null,
      showDatePicker: false,
      showTimePicker: false,
      selectedDate: null,
      selectedTime: null,
      availableTimes: [],
      loadingTimes: false,
      currentStep: 'name', // name, email, company, date, time, confirm
      userInfo: {
        name: '',
        email: '',
        phone: '',
        company: ''
      }
    }
  },
  computed: {
    minDate() {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow.toISOString().split('T')[0]
    },

    maxDate() {
      const maxDate = new Date()
      maxDate.setDate(maxDate.getDate() + 30) // 30 days from now
      return maxDate.toISOString().split('T')[0]
    },

    formatSelectedDate() {
      if (!this.selectedDate) return ''
      return new Date(this.selectedDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    allowedDates() {
      return (date) => {
        const day = new Date(date).getDay()
        // Only allow Monday (1) through Friday (5)
        return day >= 1 && day <= 5
      }
    }
  },

  mounted() {
    this.initializeChat()
  },
  methods: {
    closeModal() {
      this.isOpen = false
      this.$emit('close')
    },
    
    async initializeChat() {
      try {
        console.log('🎤 Initializing demo scheduling chat...')
        
        // Add welcome message
        this.addMessage('agent', `
          <div>
            <p>👋 Hi! I'm your AutoSyncX™ Demo Assistant. I'll help you schedule a personalized demo of our platform.</p>
            <p>Let's get started! What's your name?</p>
          </div>
        `)
        
      } catch (error) {
        console.error('❌ Error initializing demo chat:', error)
        this.$toast.error('Failed to initialize demo scheduling. Please try again.')
      }
    },

    async sendMessage() {
      if (!this.newMessage.trim() || this.isLoading) return

      const userMessage = this.newMessage.trim()
      this.addMessage('user', userMessage)
      this.newMessage = ''
      this.isTyping = true
      this.isLoading = true

      this.scrollToBottom()

      try {
        // Process the user message and generate appropriate response
        const response = await this.processUserMessage(userMessage)
        
        setTimeout(() => {
          this.addMessage('agent', response)
          this.scrollToBottom()
          this.isTyping = false
          this.isLoading = false
        }, 1500)

      } catch (error) {
        console.error('❌ Error processing message:', error)
        this.addMessage('agent', 'I apologize, but I encountered an error. Please try again.')
        this.isTyping = false
        this.isLoading = false
      }
    },

    async processUserMessage(message) {
      const trimmedMessage = message.trim()

      switch (this.currentStep) {
        case 'name':
          return this.handleNameStep(trimmedMessage)

        case 'email':
          return this.handleEmailStep(trimmedMessage)

        case 'company':
          return this.handleCompanyStep(trimmedMessage)

        case 'date':
          return this.handleDateStep(trimmedMessage)

        case 'time':
          return this.handleTimeStep(trimmedMessage)

        default:
          return this.handleDefaultStep(trimmedMessage)
      }
    },

    handleNameStep(message) {
      if (message.length < 2) {
        return "Please provide your full name."
      }

      this.userInfo.name = this.extractName(message)
      this.currentStep = 'email'
      return `Nice to meet you, ${this.userInfo.name}! What's your email address?`
    },

    handleEmailStep(message) {
      if (!this.isEmail(message)) {
        return "Please provide a valid email address."
      }

      this.userInfo.email = message
      this.currentStep = 'company'
      return `Great! What's your company name?`
    },

    handleCompanyStep(message) {
      if (message.length < 2) {
        return "Please provide your company name."
      }

      this.userInfo.company = message
      this.currentStep = 'date'
      this.showDatePicker = true
      return `Perfect! Now let's schedule your demo. Please select your preferred date from the calendar below:`
    },

    handleDateStep(message) {
      return "Please select a date from the calendar above."
    },

    handleTimeStep(message) {
      return "Please select a time from the available slots above."
    },

    handleDefaultStep(message) {
      const lowerMessage = message.toLowerCase()

      if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
        return `Hello! Let's get your demo scheduled. What's your name?`
      }

      return `I'd be happy to help you schedule a demo. What's your name?`
    },

    extractName(message) {
      const patterns = [
        /my name is (.+)/i,
        /i'm (.+)/i,
        /i am (.+)/i
      ]
      
      for (const pattern of patterns) {
        const match = message.match(pattern)
        if (match) {
          return match[1].trim()
        }
      }
      
      return message.trim()
    },

    isEmail(text) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(text.trim())
    },

    async onDateSelected(date) {
      this.selectedDate = date
      this.showDatePicker = false
      this.showTimePicker = true
      this.currentStep = 'time'

      // Load available times for selected date
      await this.loadAvailableTimesForDate(date)

      this.addMessage('agent', `Great choice! Here are the available times for ${this.formatSelectedDate}:`)
    },

    async loadAvailableTimesForDate(date) {
      this.loadingTimes = true
      try {
        // Check which times are available for this date
        const response = await axios.get(`/api/demo/available-slots?date=${date}`)

        if (response.data.flag === 1) {
          this.availableTimes = response.data.times || this.getDefaultTimes()
        } else {
          this.availableTimes = this.getDefaultTimes()
        }
      } catch (error) {
        console.error('Error loading times:', error)
        this.availableTimes = this.getDefaultTimes()
      } finally {
        this.loadingTimes = false
      }
    },

    getDefaultTimes() {
      return [
        { value: '10:00', label: '10:00 AM' },
        { value: '11:00', label: '11:00 AM' },
        { value: '14:00', label: '2:00 PM' },
        { value: '15:00', label: '3:00 PM' },
        { value: '16:00', label: '4:00 PM' }
      ]
    },

    async selectTime(time) {
      this.selectedTime = time
      this.showTimePicker = false
      this.currentStep = 'confirm'

      // Book the demo
      await this.bookDemo()
    },

    async bookDemo() {
      try {
        const timeLabel = this.availableTimes.find(t => t.value === this.selectedTime)?.label || this.selectedTime

        const bookingData = {
          name: this.userInfo.name,
          email: this.userInfo.email,
          company: this.userInfo.company,
          date: this.selectedDate,
          time: this.selectedTime
        }

        const response = await axios.post('/api/demo/book-slot', bookingData)

        if (response.data.flag === 1) {
          this.addMessage('agent', `
            <div>
              <p>🎉 Excellent! Your demo has been scheduled successfully!</p>
              <p><strong>📅 ${this.formatSelectedDate}</strong></p>
              <p><strong>⏰ ${timeLabel}</strong></p>
              <p>You'll receive a confirmation email shortly with:</p>
              <ul>
                <li>📧 Calendar invite</li>
                <li>🔗 Meeting link</li>
                <li>📋 Preparation materials</li>
              </ul>
              <p>We're excited to show you how AutoSyncX™ can transform your dealership! 🚗✨</p>
            </div>
          `)
        } else {
          this.addMessage('agent', 'Sorry, that time slot is no longer available. Let me show you other options...')
          this.showTimePicker = true
          this.currentStep = 'time'
        }
      } catch (error) {
        console.error('Error booking demo:', error)
        this.addMessage('agent', 'There was an error booking your demo. Please try selecting another time.')
        this.showTimePicker = true
        this.currentStep = 'time'
      }
    },



    addMessage(type, text) {
      this.messages.push({
        id: Date.now(),
        type: type,
        text: text,
        timestamp: new Date()
      })
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString([], {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped>
.demo-scheduling-modal {
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.agent-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.agent-name {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.agent-title {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

.modal-content {
  padding: 0;
  height: 600px;
  display: flex;
  flex-direction: column;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8f9fa;
}

.message {
  margin-bottom: 16px;
  display: flex;
}

.message.user {
  justify-content: flex-end;
}

.message.agent {
  justify-content: flex-start;
}

.message-content {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
}

.message.user .message-content {
  background: #667eea;
  color: white;
  border-bottom-right-radius: 4px;
}

.message.agent .message-content {
  background: white;
  color: #333;
  border: 1px solid #e0e0e0;
  border-bottom-left-radius: 4px;
}

.message-text {
  line-height: 1.4;
}

.message-time {
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #999;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
}

.date-picker-section {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: white;
}

.date-picker-section h4 {
  margin: 0 0 16px 0;
  color: #333;
  text-align: center;
}

.time-picker-section {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: white;
}

.time-picker-section h4 {
  margin: 0 0 16px 0;
  color: #333;
  text-align: center;
}

.time-slots-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.time-slot-btn {
  min-width: 120px !important;
  height: 48px !important;
  font-weight: 600 !important;
}

.chat-input {
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  background: white;
}

.send-btn {
  color: #667eea !important;
}
</style>
